/**
 * Centralized API configuration
 * 
 * USAGE GUIDE:
 * ------------
 * Use API_BASE for all API calls. It includes the /api prefix.
 * 
 * Example:
 *   import { API_BASE } from '../lib/config';
 *   fetch(`${API_BASE}/dashboard/stats`)  // → https://domain.com/api/dashboard/stats
 * 
 * DO NOT use API_URL directly for API calls - it lacks the /api prefix.
 * API_URL is only exported for legacy compatibility.
 */

// Raw backend URL from environment (DO NOT use directly for API calls)
export const API_URL = process.env.REACT_APP_BACKEND_URL;

// Use this for all API calls - includes /api prefix
export const API_BASE = `${API_URL}/api`;

/**
 * Get authentication headers for API requests
 * @returns {Object} Headers object with Content-Type and Authorization
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

/**
 * Get token from localStorage
 * @returns {string|null} The authentication token
 */
export const getToken = () => localStorage.getItem('token');

/**
 * Build URL with query parameters
 * @param {string} endpoint - API endpoint (without /api prefix)
 * @param {Object} params - Query parameters
 * @returns {string} Full URL with query string
 */
export const buildUrl = (endpoint, params = {}) => {
  const queryString = new URLSearchParams(
    Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  return `${API_BASE}/${endpoint}${queryString ? `?${queryString}` : ''}`;
};

/**
 * Standard fetch wrapper with error handling
 * @param {string} url - Full URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} Parsed JSON response
 */
export const apiFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const error = new Error(`API Error: ${response.status}`);
    error.status = response.status;
    try {
      error.data = await response.json();
    } catch {
      error.data = null;
    }
    throw error;
  }
  
  return response.json();
};
